import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    CollectionReference,
    DocumentData,
    DocumentSnapshot,
    FirestoreError,
    QueryConstraint,
    getCountFromServer,
    limit,
    onSnapshot,
    query,
    startAfter,
} from "firebase/firestore";

/**
 * Live, cursor-paginated Firestore list — no Typesense.
 *
 * - The current page is an onSnapshot listener, so rows update in place as orders move
 *   through the lifecycle (status, lane, commissions...).
 * - Pages are anchored by the last document of the previous page (startAfter). New rows
 *   arriving at the top show up on page 1 live; deeper pages keep their anchor.
 * - One extra document is fetched to know whether a next page exists, so no count is needed
 *   to paginate. The total (getCountFromServer) is fetched separately and refreshed
 *   (debounced) whenever the visible page changes; it is only a label.
 * - `constraints` must be server-side filters + orderBy that have a composite index in
 *   firestore.indexes.json. `key` must change whenever the constraints change (it resets to page 1).
 */
export interface LivePage<T> {
    rows: T[];
    loading: boolean;
    error?: FirestoreError;
    page: number; // 0-based
    hasNext: boolean;
    hasPrev: boolean;
    next: () => void;
    prev: () => void;
    first: () => void;
    total?: number;
    pageSize: number;
}

/**
 * Page and cursors per list key, kept while the app is open: opening an order and coming back
 * (Back button or browser back) returns to the same page instead of page 1. Not kept across a reload.
 */
const pageMemory = new Map<string, { page: number; cursors: Array<DocumentSnapshot | undefined> }>();

export function useLivePagedQuery<T extends { id?: string }>(
    ref: CollectionReference<DocumentData>,
    constraints: QueryConstraint[],
    key: string,
    pageSize = 25,
): LivePage<T> {
    const [page, setPage] = useState(() => pageMemory.get(key)?.page ?? 0);
    const cursors = useRef<Array<DocumentSnapshot | undefined>>(pageMemory.get(key)?.cursors ?? [undefined]);
    const shownKey = useRef(key);
    const [rows, setRows] = useState<T[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<FirestoreError | undefined>();
    const [hasNext, setHasNext] = useState(false);
    const [total, setTotal] = useState<number | undefined>();
    const lastDocOfPage = useRef<DocumentSnapshot | undefined>(undefined);
    const countTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    // reset when the filters change (not on mount: that may be a return to a remembered page)
    useEffect(() => {
        if (shownKey.current === key) return;
        shownKey.current = key;
        const remembered = pageMemory.get(key);
        cursors.current = remembered?.cursors ?? [undefined];
        setPage(remembered?.page ?? 0);
        setTotal(undefined);
    }, [key]);
    useEffect(() => { pageMemory.set(key, { page, cursors: cursors.current }); }, [key, page]);

    const refreshCount = useCallback(() => {
        if (countTimer.current) clearTimeout(countTimer.current);
        countTimer.current = setTimeout(() => {
            getCountFromServer(query(ref, ...constraints))
                .then((snap) => setTotal(snap.data().count))
                .catch(() => setTotal(undefined));
        }, 1500);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, ref]);

    useEffect(() => {
        setLoading(true);
        const cursor = cursors.current[page];
        const q = query(ref, ...constraints, ...(cursor ? [startAfter(cursor)] : []), limit(pageSize + 1));
        const unsubscribe = onSnapshot(q, (snap) => {
            const docs = snap.docs;
            setHasNext(docs.length > pageSize);
            const pageDocs = docs.slice(0, pageSize);
            lastDocOfPage.current = pageDocs[pageDocs.length - 1];
            setRows(pageDocs.map((d) => ({ ...(d.data() as T), id: d.id })));
            setError(undefined);
            setLoading(false);
            refreshCount(); // debounced; keeps "x of N" honest as rows enter/leave the filter
        }, (err) => {
            console.error("useLivePagedQuery", key, err);
            setError(err);
            setLoading(false);
        });
        return () => unsubscribe();
        // constraints are captured through `key`
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, key, page, pageSize]);

    useEffect(() => () => { if (countTimer.current) clearTimeout(countTimer.current); }, []);

    const next = useCallback(() => {
        if (!hasNext || !lastDocOfPage.current) return;
        cursors.current[page + 1] = lastDocOfPage.current;
        setPage(page + 1);
    }, [hasNext, page]);
    const prev = useCallback(() => setPage((p) => Math.max(0, p - 1)), []);
    const first = useCallback(() => setPage(0), []);

    return useMemo(() => ({ rows, loading, error, page, hasNext, hasPrev: page > 0, next, prev, first, total, pageSize }),
        [rows, loading, error, page, hasNext, next, prev, first, total, pageSize]);
}
